import {
  AsyncPipe,
  NgClass,
  NgFor,
  NgIf,
  TitleCasePipe,
} from '@angular/common';
import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import {
  CLIENT_APP_ID,
  ClientAppId,
  DossierStateHistory,
} from '@sc/data-access-models';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import { dayjs } from '@sc/utils';
import { get } from 'lodash-es';
import { MomentParseZonePipe } from '../../pipes';
import { IconComponent } from '../icon';

export interface DossierSearchHistory {
  date: string;
  userName: string;
  userType: string;
  state: string;
  displayModifiedFields: boolean;
  modifiedFields: DossierSearchHistoryModifiedFields[];
}
export interface DossierSearchHistoryModifiedFields {
  fieldName: string;
  fieldCode: string;
  oldValue: any;
  newValue: any;
}

@Component({
  selector: 'sc-dossier-search-history',
  styleUrls: ['./dossier-search-history.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgIf,
    NgFor,
    NgClass,
    IconComponent,
    MomentParseZonePipe,
    MatTableModule,
    TranslateModule,
    LocaleDatePipe,
    AsyncPipe,
    TitleCasePipe,
  ],
  template: ` <div class="main-container">
    <div class="dialog-header-container">
      <h1
        mat-dialog-title
        id="dossier-search-history-title-id"
        class="sc-design-title-h1"
      >
        {{ 'DossierSearchHistory.mainTitle' | translate }}
      </h1>

      <button type="button" class="close-btn" (click)="dialogRef.close()">
        <span class="sr_only">{{
          'GlobalApplicativeConstants.accessibility.closeDialog'
            | translate
              : { dialogTitle: 'AbandonDossierSheet.mainTitle' | translate }
        }}</span>
        <sc-icon name="close"></sc-icon>
      </button>
    </div>

    <div class="origin-infos-container">
      <h2 class="sc-design-text-2">
        {{ 'DossierSearchHistory.origin.title' | translate }}
      </h2>

      <p>
        {{
          'DossierSearchHistory.origin.globalOrigin'
            | translate
              : {
                  originValue:
                    'GlobalApplicativeConstants.DossierOrigins.' +
                      getDossierGlobalOrigin() | translate
                }
        }}
      </p>

      <p *ngIf="dialogData?.dossier?.creationData?.email">
        {{
          'DossierSearchHistory.origin.creator'
            | translate
              : {
                  email: dialogData.dossier.creationData.email
                }
        }}
      </p>
    </div>

    <section
      id="cd-timeline"
      class="cd-container"
      *ngIf="formatedHistory?.length"
    >
      <div class="cd-timeline-block" *ngFor="let item of formatedHistory">
        <div class="cd-timeline-img cd-picture"></div>

        <div class="cd-timeline-content">
          <h2>
            {{
              item.date
                | parseZoneTimeString: timezone
                | localeDate: 'longDate'
                | async
                | titlecase
            }}
            -
            {{
              item.date
                | parseZoneTimeString: timezone
                | localeDate: 'HH:mm:ss'
                | async
                | titlecase
            }}
          </h2>
          <span class="cd-date" *ngIf="item?.userName && item?.userType"
            >{{ item?.userName }} - {{ formatUserType(item) | translate }}</span
          >
          <span class="cd-date" *ngIf="!item?.userName && !item?.userType">
            {{ 'DossierSearchHistory.modifyAuto' | translate }}</span
          >
          <p>
            {{
              'GlobalApplicativeConstants.dossierStates.' +
                item?.state?.toLowerCase() | translate
            }}
          </p>
          <div
            class="see-more"
            *ngIf="item?.modifiedFields?.length"
            (click)="item.displayModifiedFields = !item?.displayModifiedFields"
          >
            {{ 'DossierSearchHistory.seeMoreBtn' | translate }}
          </div>
          <div>
            <table
              *ngIf="item.displayModifiedFields"
              mat-table
              [dataSource]="
                getModifiedFieldsMatTableDataSource(item?.modifiedFields)
              "
            >
              <span *ngFor="let column of columnsObj">
                <ng-container [matColumnDef]="column?.ihm">
                  <th mat-header-cell *matHeaderCellDef>{{ column?.ihm }}</th>
                  <td
                    mat-cell
                    *matCellDef="let element"
                    [ngClass]="{
                      oldValueCell: column?.code === 'oldValue',
                      newValueCell: column?.code === 'newValue'
                    }"
                  >
                    <p class="attribute-value-display">
                      {{ element[column?.code] }}
                    </p>
                  </td>
                </ng-container>
              </span>
              <tr mat-header-row *matHeaderRowDef="columnsToDisplay"></tr>
              <tr mat-row *matRowDef="let row; columns: columnsToDisplay"></tr>
            </table>
          </div>
        </div>
      </div>
    </section>
  </div>`,
})
export class DossierSearchHistoryComponent implements OnInit {
  columnsToDisplay;
  columnsObj = [
    {
      ihm: this.translator.getInstantTranslation(
        'DossierSearchHistory.updatedFieldColumns.fieldName'
      ),
      code: 'fieldName',
    },
    {
      ihm: this.translator.getInstantTranslation(
        'DossierSearchHistory.updatedFieldColumns.oldValue'
      ),
      code: 'oldValue',
    },
    {
      ihm: this.translator.getInstantTranslation(
        'DossierSearchHistory.updatedFieldColumns.newValue'
      ),
      code: 'newValue',
    },
  ];
  formatedHistory: DossierSearchHistory[];
  stateHistory: DossierStateHistory[];
  timezone: string;
  matTableDataSource: any = {};

  constructor(
    public dialogRef: MatDialogRef<DossierSearchHistoryComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData,
    private readonly translator: TranslatorService,
    public timezoneService: TimezoneService,
    private readonly configurationService: ConfigurationService,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId
  ) {}

  ngOnInit() {
    this.columnsToDisplay = this.columnsObj?.map((obj) => obj?.ihm);
    this.stateHistory = this.dialogData?.dossier?.stateHistory;
    this.timezone = this.timezoneService.getFitterTimezone(
      this.dialogData?.dossier?.appointment
    );
    this.formatedHistory = this.initHistory();
  }

  getDossierGlobalOrigin() {
    return this.configurationService.getDossierGlobalOriginFromCreationData(
      this.dialogData?.dossier?.creationData,
      this.clientAppId
    );
  }

  getModifiedFieldsMatTableDataSource(data: any) {
    const currentModifTable = JSON.stringify(data);
    if (this.matTableDataSource[currentModifTable]) {
      return this.matTableDataSource[currentModifTable];
    } else {
      this.matTableDataSource[currentModifTable] = data?.length
        ? new MatTableDataSource(data)
        : new MatTableDataSource([]);
      return this.matTableDataSource[currentModifTable];
    }
  }

  getDeepKeys(obj) {
    let keys = [];
    for (const key in obj) {
      keys.push(key);
      if (typeof obj[key] === 'object') {
        const subkeys = this.getDeepKeys(obj[key]);
        keys = keys.concat(
          subkeys.map(function (subkey) {
            return key + '.' + subkey;
          })
        );
      }
    }
    return keys;
  }

  initHistory(): DossierSearchHistory[] {
    //All displayable updated fields are in dictionnary
    //Here we get keys to parse stateHistory and display only interested fields
    const fieldsToDisplay = this.getDeepKeys(
      this.translator.getInstantTranslation(
        'DossierSearchHistory.updatedFields'
      )
    );
    return this.stateHistory?.length
      ? this.stateHistory
          ?.sort((st1, st2) =>
            new Date(st1?.timestamp) < new Date(st2?.timestamp) ? 1 : -1
          )
          ?.map((history) => {
            let data: DossierSearchHistory;
            if (history) {
              data = {
                date: history?.timestamp?.toString(),
                userName: history?.userName,
                userType: history?.userType,
                state: history?.state,
                modifiedFields: [],
                displayModifiedFields: false,
              };
              if (history?.data) {
                //All updated attributes in stateHistory.data has 3 potential suffixes __old vs __new or just __added
                //First we get all keys in data object then we filter on suffixes
                const keys = this.getDeepKeys(history.data)?.filter((data) => {
                  return (
                    data?.includes('__old') ||
                    data?.includes('__new') ||
                    data?.includes('__added')
                  );
                });

                for (const key of keys) {
                  let label = key
                    .replaceAll('__old', '.')
                    .replaceAll('__new', '.')
                    .replaceAll('__added', '.')
                    .replaceAll('..', '.');
                  if (label.substring(label.length - 1) === '.')
                    label = label.substring(0, label.length - 1);
                  const field = fieldsToDisplay.find(
                    (field) => label === field
                  );
                  let modifiedField;
                  if (field && key.includes('__old')) {
                    const newValue = this.getParsedValue(
                      history?.data,
                      key.replaceAll('__old', '__new')
                    );
                    modifiedField =
                      newValue != null && newValue != undefined
                        ? {
                            fieldName: this.getParsedFieldName(field),
                            fieldCode: field,
                            oldValue: this.getParsedValue(history?.data, key),
                            newValue: newValue,
                          }
                        : null;
                  } else if (field && key.includes('__added')) {
                    const fieldName = this.getParsedFieldName(field);

                    if (typeof fieldName !== 'object') {
                      const newValue = this.getParsedValue(history?.data, key);
                      modifiedField = newValue
                        ? {
                            fieldName,
                            fieldCode: field,
                            oldValue: null,
                            newValue: newValue,
                          }
                        : null;
                    }
                  }

                  if (modifiedField) {
                    data?.modifiedFields?.push(modifiedField);
                  }
                }
              }
            }
            return data;
          })
          .filter((data) => data.modifiedFields.length > 0)
      : [];
  }

  formatUserType(historyItem: DossierSearchHistory) {
    if (historyItem.userType === 'API') {
      return `GlobalApplicativeConstants.DossierOrigins.${this.getDossierGlobalOrigin()}`;
    }

    return `GlobalApplicativeConstants.userType.${historyItem.userType}`;
  }

  getParsedFieldName(field: any) {
    if (field?.includes('ownerShip') && !field.endsWith('type')) {
      return this.translator.getInstantTranslation(
        `DossierSearchHistory.updatedFields.${field}.type`
      );
    }
    return this.translator.getInstantTranslation(
      `DossierSearchHistory.updatedFields.${field}`
    );
  }

  getParsedValue(data, field: string) {
    let value = get(data, field);
    if (value !== undefined && value !== null) {
      if (
        field?.includes('interventionLocation') &&
        typeof value === 'object'
      ) {
        value = value?.address;
      } else if (field?.includes('status')) {
        value = this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.appointmentStatus.' + value?.toLowerCase()
        );
      } else if (field?.toLowerCase()?.includes('time')) {
        value = dayjs(value)
          ?.tz(
            this.dialogData?.dossier?.appointment?.timezone ||
              this.dialogData?.dossier?.requestAppointment?.timezone ||
              'Etc/UTC'
          )
          .format('LLL');
      } else if (field?.includes('assignedReplacementVehicle')) {
        value = value?.name + ' (' + value?.licensePlateNumber + ')';
      } else if (field?.includes('impacts') && typeof value !== 'boolean') {
        if (typeof value === 'object') {
          const key = Object.keys(value)[0];
          value = this.translator.getInstantTranslation(
            `GlobalApplicativeConstants.sinisterGlass.glassImpact.${value[key]}`
          );
        } else {
          value = this.translator.getInstantTranslation(
            `GlobalApplicativeConstants.sinisterGlass.glassImpact.${value}`
          );
        }
      } else if (field?.includes('vehicleType')) {
        value = this.translator.getInstantTranslation(
          `CustomerDeclaration.VehicleTypePage.vehicleTypeNames.${value?.toLowerCase()}`
        );
      } else if (field?.includes('ownerShip')) {
        value = this.translator.getInstantTranslation(
          `GlobalApplicativeConstants.ownerShipType.${value?.type || value}`
        );
      } else if (field?.includes('operationType')) {
        value = this.translator.getInstantTranslation(
          `GlobalApplicativeConstants.operationType.${value?.toLowerCase()}`
        );
      } else if (typeof value === 'string' && field?.includes('glass')) {
        value = this.translator.getInstantTranslation(
          `GlobalApplicativeConstants.sinisterGlass.glass.${value}.name`
        );
      } else if (
        typeof value === 'string' &&
        field?.includes('partAvailability')
      ) {
        value = this.translator.getInstantTranslation(
          `GlobalApplicativeConstants.partAvailabilities.${value}`
        );
      } else if (typeof value === 'boolean') {
        value = value
          ? this.translator.getInstantTranslation(
              `GlobalApplicativeConstants.action.yes`
            )
          : this.translator.getInstantTranslation(
              `GlobalApplicativeConstants.action.no`
            );
      } else if (typeof value === 'object') {
        value = null;
      }
    }

    return value;
  }
}
